import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ObjectsService } from '../../../../objects.service';
import { ReplaySubject } from 'rxjs';
import { TranslateLanguageService } from '@core/services/translate-language.service';
import { takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import { latLng } from 'leaflet';

@Component({
    selector: 'app-object-map-selector',
    templateUrl: './object-map-selector.component.html',
    styleUrls: ['./object-map-selector.component.less']
})

export class ObjectMapSelectorComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() onClickOnMap;
    @Input() updateAddress;
    @Input() refreshType;
    @Input() typeObject = 1;
    @Input() currentAddress = null;
    reInitMap = true;
    addressString = '';
    currentCoords = L.latLng(0, 0);
    baseLayer = L.marker(L.latLng(0, 0), {
        icon: L.icon({
            iconSize: [45, 62],
            iconAnchor: [13, 62],
            iconUrl: `assets/icons/map/${this.typeObject}.png`,
            shadowUrl: 'assets/icons/map/marker-shadow.png'
        })
    });
    options = {
        layers: [
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 18,
                attribution: ''
            }),
            this.baseLayer
        ],
        zoom: 13,
        center: L.latLng(55.752429, 37.620106)
    };
    map;
    lang;
    protected readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(private objectsService: ObjectsService,
                private translateLanguageService: TranslateLanguageService) {
    }

    ngOnInit(): void {
        this.translateLanguageService.currentLanguage$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((lang) => {
                this.lang = lang;
            });

        this.refreshType
            .pipe(takeUntil(this.destroyed$))
            .subscribe((type) => {
                this.typeObject = type;
                this.baseLayer.setIcon(
                    L.icon({
                        iconSize: [45, 62],
                        iconAnchor: [13, 62],
                        iconUrl: `assets/icons/map/${type === 0 ? 1 : type}.png`,
                        shadowUrl: 'assets/icons/map/marker-shadow.png'
                    })
                );
            });

        this.updateAddress
            .pipe(takeUntil(this.destroyed$))
            .subscribe((address) => {
                if (address?.coordinates && this.map) {
                    let fakeEvent = L.latLng({lat: address?.coordinates[0], lng: address?.coordinates[1]});
                    this.setNewMapObject(fakeEvent);
                }
            });
    }

    ngAfterViewInit(): void {
        this.initMap();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes?.currentAddress?.previousValue?.unrestricted_value !== changes?.currentAddress?.currentValue?.unrestricted_value
            && this.currentAddress !== null
            && changes?.currentAddress?.previousValue?.unrestricted_value !== undefined
        ) {
            if (this.map) {
                let fakeEvent = this.currentAddress ? L.latLng(latLng(this.currentAddress.coordinates[0], this.currentAddress.coordinates[1])) : null;
                this.setNewMapObject(fakeEvent);
            } else {
                this.initMap();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    initMap(): void {
        let coords = this.currentAddress ? latLng(this.currentAddress.coordinates[0], this.currentAddress.coordinates[1]) : null;
        if (this.currentAddress !== null) {
            this.addressString = this.currentAddress.unrestricted_value;
            this.baseLayer
                .setLatLng(coords)
                .setIcon(
                    L.icon({
                        iconSize: [45, 62],
                        iconAnchor: [13, 62],
                        iconUrl: `assets/icons/map/${this.getIcon(this.typeObject, this.objectsService.currentNode)}.png`,
                        shadowUrl: 'assets/icons/map/marker-shadow.png',
                        className: this.objectsService.currentNode?.devices_info?.total_count_devices === 0 ? 'gray' : ''
                    })
                );
        } else {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    {
                        const crd = latLng(pos.coords.latitude, pos.coords.longitude);
                        this.setNewMapObject(crd);
                    }
                },
                (err) => {
                    console.warn(`ERROR(${err.code}): ${err.message}`);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                }
            );
        }

        this.options = {
            layers: [
                L.tileLayer(`https://core-renderer-tiles.maps.yandex.net/tiles?l=map&v=21.06.04-0-b210520094930&x={x}&y={y}&z={z}&scale=2&lang=ru`, {
                    minZoom: 2,
                    maxZoom: 18,
                    attribution: ''
                }),
                this.baseLayer
            ],
            zoom: 13,
            center: coords ?? L.latLng(55.752429, 37.620106)
        };
        this.map = L.map('add-object-map', {
            ...this.options,
        });

        // включает режим смещения координат для этого слоя карт
        this.map.options.crs = L.CRS.EPSG3395;

        this.map.on('click', (event) => {
            this.handlerClickOnMap(event);
        });
        this.map.panTo(this.options.center);
    }

    handlerClickOnMap($event: L.LeafletMouseEvent) {
        this.setNewMapObject($event.latlng);
    }

    private setNewMapObject(latLngCoords: any) {
        this.currentCoords = latLngCoords ?? L.latLng(55.752429, 37.620106);
        this.baseLayer.setLatLng(this.currentCoords).setIcon(
            L.icon({
                iconSize: [45, 62],
                iconAnchor: [13, 62],
                iconUrl: `assets/icons/map/${this.getIcon(this.typeObject, this.objectsService.currentNode, true)}.png`,
                shadowUrl: 'assets/icons/map/marker-shadow.png',
                className: this.objectsService.currentNode?.devices_info?.total_count_devices === 0 ? 'gray' : ''
            })
        );
        this.map.panTo(this.currentCoords, {duration: 1.2});
        this.objectsService.getAddressByCoords(this.currentCoords, this.lang).subscribe((result: any) => {
            if (result?.error) {
                const objectAddress = {
                    country: undefined,
                    city: undefined,
                    city_district: undefined,
                    region: undefined,
                    street: undefined,
                    house: undefined,
                    area: undefined,
                    settlement: undefined,
                    coordinates: [this.currentCoords?.lat, this.currentCoords?.lng],
                    unrestricted_value: undefined
                };
                this.onClickOnMap.emit(objectAddress);
            } else {
                const objectAddress = {
                    country: '',
                    city_district: result?.address?.city_district || '',
                    city: result?.address?.city || '',
                    region: result?.address?.state || '',
                    street: result?.address?.road || '',
                    house: result?.address?.house_number || '',
                    area: result?.address?.county || '',
                    settlement: result?.address?.village || '',
                    coordinates: [this.currentCoords?.lat, this.currentCoords?.lng],
                    unrestricted_value: this.getUnrestrictedValue(result?.display_name),
                };
                this.onClickOnMap.emit(objectAddress);
            }
        });
    }

    getUnrestrictedValue(v: string | null | undefined): string {
        if (!v) {
            return '';
        }
        const index = typeof v === 'string' ? v.lastIndexOf(',') : 0;
        return v ? v.slice(0, index) : '';
    }

     getIcon(typeObject: number, node: any, newSet = false) {
         const onlineDevices = node?.devices_info?.count_active_devices_online;
         const offlineDevices = node?.devices_info?.count_active_devices_offline;
         const isEmpty = node?.devices_info?.total_count_devices === 0;
         const withoutTypeObject = typeObject === 0 || typeObject === undefined;
         let icon;
         if (newSet) {
             icon = withoutTypeObject ? 'without_type_object' : `${typeObject}`;
         } else {
             if (onlineDevices === 0 && !isEmpty) {
                 icon = withoutTypeObject ? 'without_type_object_offline' : `${typeObject}` + '_offline';
             } else if (onlineDevices !== 0 && offlineDevices !== 0 && !isEmpty) {
                 icon = withoutTypeObject ? 'without_type_object_something' : `${typeObject}` + '_something';
             } else if (isEmpty) {
                 icon = withoutTypeObject ? 'without_type_object_offline' : `${typeObject}` + '_offline';
             } else {
                 icon = withoutTypeObject ? 'without_type_object' : `${typeObject}`;
             }
         }


        return icon;
    }
}
